import React from "react";
import { useTranslation } from "react-i18next";
import {
  GlobalSearchResult,
  GlobalSearchEntityTypes,
  RecentSearchItem
} from "../types";
import { Ellipse, Enter, ExternalLink } from "@dreamhub/designsystem/icons";
import { clsx } from "clsx";
import IconContainer from "@/components/ui/icon-container";
import LogoIconContainer from "@/components/ui/logo-icon-container";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import {
  entityIcons,
  getEntityIcon,
  getEntityTypeLabel
} from "../utils/entityIcons";
import { getEnhancedRelativeTime } from "@/utils/datetime";
import { cn } from "@/utils/utils";
import { useSearchFieldLabels } from "../hooks/useSearchFieldLabels";

const FieldLabel: React.FC<{
  entityType: GlobalSearchEntityTypes;
  fieldKey: string;
}> = ({ entityType, fieldKey }) => {
  const { getFieldLabel } = useSearchFieldLabels();
  return <>{getFieldLabel(entityType, fieldKey)}</>;
};

type SearchItemData = GlobalSearchResult | RecentSearchItem;

interface SearchItemProps {
  item: SearchItemData;
  isSelected: boolean;
  onClick: () => void;
  onOpenInNewTab?: () => void;
  variant: "search-result" | "recent-search";
  isOtherMatch?: boolean;
}

const isRecentSearchItem = (item: SearchItemData): item is RecentSearchItem => {
  return "query" in item && "searchedAt" in item;
};

const isGlobalSearchResult = (
  item: SearchItemData
): item is GlobalSearchResult => {
  return "highlights" in item && "metadata" in item;
};

const isOtherResult = (item: SearchItemData): item is GlobalSearchResult => {
  return "highlights" in item;
};

export const SearchItem = React.forwardRef<HTMLDivElement, SearchItemProps>(
  (
    {
      item,
      isSelected,
      onClick,
      onOpenInNewTab,
      variant,
      isOtherMatch = false
    },
    ref
  ) => {
    const { t } = useTranslation();

    const handleEnterClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      onClick();
    };

    const handleExternalLinkClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (onOpenInNewTab) {
        onOpenInNewTab();
      }
    };

    const getFirstNonNameHighlight = (): {
      field: string;
      value: string;
    } | null => {
      if (!isOtherResult(item)) return null;
      const highlights = item.highlights;
      for (const [field, value] of Object.entries(highlights)) {
        if (field !== "name" && value) {
          return { field, value };
        }
      }
      return null;
    };

    const getDisplayData = () => {
      if (isRecentSearchItem(item)) {
        return {
          name: item.resultName || item.query,
          entityType: item.resultEntityType,
          icon: getEntityIcon(item.resultEntityType),
          showExternalLink: !!(
            onOpenInNewTab &&
            item.resultId &&
            item.resultEntityType
          )
        };
      } else {
        const Icon = entityIcons[item.entityType];
        return {
          name: item.name,
          entityType: item.entityType,
          icon: Icon ? <Icon /> : null,
          showExternalLink: !!onOpenInNewTab
        };
      }
    };

    const displayData = getDisplayData();

    const getVariantStyles = () => {
      if (variant === "search-result") {
        return {
          container: clsx(
            "flex items-center h-[62px] p-2 pr-4 cursor-pointer border transition-colors hover:bg-surface-hover-soft rounded-2xl",
            isSelected
              ? "bg-surface-hover-soft rounded-2xl border-primary"
              : "border-transparent"
          ),
          iconContainer: "flex-shrink-0 mr-3",
          iconSize: "md" as const,
          textVariant: "lgBody" as const,
          showButtons: true
        };
      } else {
        return {
          container: clsx(
            "flex items-center gap-3 px-2 py-2 rounded-lg transition-colors text-left group cursor-pointer",
            isSelected
              ? "bg-surface-hover-soft border border-primary"
              : "hover:bg-surface-elevation2 border border-transparent"
          ),
          iconContainer: "",
          iconSize: "sm" as const,
          textVariant: "body" as const,
          showButtons: isSelected
        };
      }
    };

    const styles = getVariantStyles();

    return (
      <div
        ref={ref}
        role="option"
        aria-selected={isSelected}
        className={styles.container}
        onClick={onClick}
        data-test={`global-search-item-${variant}`}
      >
        <div className={styles.iconContainer}>
          <LogoIconContainer
            index={-1}
            iconSize={styles.iconSize}
            icon={displayData.icon}
            className={
              variant === "recent-search" ? "w-6 h-6 rounded-[6px]" : undefined
            }
            innerClassName={
              variant === "recent-search" ? "rounded-[6px]" : undefined
            }
          />
        </div>

        <div className="flex-1 min-w-0">
          {variant === "search-result" && isGlobalSearchResult(item) ? (
            <>
              <Text variant={styles.textVariant} className="truncate">
                <span
                  className="[&_mark]:bg-blue-700 [&_mark]:text-white [&_em]:bg-blue-700 [&_em]:text-white [&_strong]:bg-blue-700 [&_strong]:text-white"
                  dangerouslySetInnerHTML={{
                    __html: item.highlights.name ?? item.name
                  }}
                />
              </Text>
              <Text variant="smBodySecondary" className="mt-1 capitalize">
                {getEntityTypeLabel(item.entityType)}
              </Text>
            </>
          ) : (
            <div className={isOtherMatch ? "flex flex-col" : "block"}>
              <div
                className={cn(
                  "flex flex-col",
                  isOtherMatch && "flex-row gap-2 items-center"
                )}
              >
                <Text variant={styles.textVariant}>{displayData.name}</Text>
                {displayData.entityType && (
                  <Text variant="smBodySecondary">
                    {`${isOtherMatch ? "(" : ""}${getEntityTypeLabel(displayData.entityType)}${isOtherMatch ? ")" : ""}`}
                  </Text>
                )}
              </div>
              {isOtherMatch &&
                isOtherResult(item) &&
                (() => {
                  const highlight = getFirstNonNameHighlight();
                  if (!highlight) return null;
                  return (
                    <div className="flex gap-2 items-center">
                      <Text variant="bodySecondary" className="truncate">
                        <span
                          className="[&_mark]:bg-purple-600 [&_mark]:text-secondary [&_em]:bg-purple-600 [&_em]:text-secondary [&_strong]:bg-purple-600 [&_strong]:text-secondary"
                          dangerouslySetInnerHTML={{ __html: highlight.value }}
                        />
                      </Text>
                      <IconContainer className="size-[5px]" color="tertiary">
                        <Ellipse />
                      </IconContainer>
                      <Text variant="bodyTertiary">
                        <FieldLabel
                          entityType={item.entityType}
                          fieldKey={highlight.field}
                        />
                      </Text>
                    </div>
                  );
                })()}
            </div>
          )}
        </div>

        {variant === "recent-search" && isRecentSearchItem(item) && (
          <Text variant="smBodySecondary">
            {getEnhancedRelativeTime(t, item.searchedAt as any)}
          </Text>
        )}

        {styles.showButtons && (
          <div className="flex-shrink-0 ml-3 flex items-center gap-2">
            {displayData.showExternalLink && (
              <Button
                onClick={handleExternalLinkClick}
                variant="icon"
                size="default"
                tooltipText={t("globalSearch.searchResultItem.openInNewTab")}
                className="h-7 w-7 p-1"
                data-test="global-search-item-open-new-tab-button"
              >
                <IconContainer size="sm">
                  <ExternalLink />
                </IconContainer>
              </Button>
            )}
            <Button
              onClick={handleEnterClick}
              variant="icon"
              size="default"
              tooltipText={t("globalSearch.searchResultItem.open")}
              className="h-7 w-7 p-1"
              data-test="global-search-item-open-button"
            >
              <IconContainer size="sm">
                <Enter />
              </IconContainer>
            </Button>
          </div>
        )}
      </div>
    );
  }
);

SearchItem.displayName = "SearchItem";
